/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod'

// =====================================
// OSNOVNI API RESPONSE WRAPPERS
// =====================================

// Single resource response { data: T }
export const ApiSingleResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T
) =>
  z.object({
    data: dataSchema,
  })

// Collection response { data: T[] }
export const ApiCollectionResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T
) =>
  z.object({
    data: z.array(dataSchema),
  })

// Paginated response (Laravel style)
export const ApiPaginatedResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T
) =>
  z.object({
    data: z.array(dataSchema),
    meta: z.object({
      current_page: z.number(),
      last_page: z.number(),
      per_page: z.number(),
      total: z.number(),
      from: z.number().nullable(),
      to: z.number().nullable(),
    }),
  })

export const ApiLookupResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T
) =>
  z.object({
    list: z.array(dataSchema),
    selected: z.array(dataSchema),
  })

// =====================================
// ERROR RESPONSES
// =====================================

// HTTP Status Codes enum
export const HttpStatusCode = z.enum([
  '200',
  '201',
  '204', // Success
  '400',
  '401',
  '403',
  '404',
  '422',
  '429', // Client errors
  '500',
  '502',
  '503', // Server errors
])

export type HttpStatusCode = z.infer<typeof HttpStatusCode>

// Base error response
export const BaseApiErrorSchema = z.object({
  message: z.string(),
  status: z.number(),
  code: z.string().optional(), // Custom error code
  details: z.any().optional(),
  timestamp: z.string().datetime().optional(),
})

// Validation error response (422)
export const ApiValidationErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(422),
  errors: z.record(z.array(z.string())), // { field: ["error1", "error2"] }
})

// Unauthorized error (401)
export const ApiUnauthorizedErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(401),
  code: z.literal('UNAUTHORIZED').optional(),
})

// Forbidden error (403)
export const ApiForbiddenErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(403),
  code: z.literal('FORBIDDEN').optional(),
})

// Not Found error (404)
export const ApiNotFoundErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(404),
  code: z.literal('NOT_FOUND').optional(),
})

// Rate Limit error (429)
export const ApiRateLimitErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(429),
  code: z.literal('RATE_LIMIT_EXCEEDED').optional(),
  retry_after: z.number().optional(), // Seconds to wait
})

// Server error (500)
export const ApiServerErrorSchema = BaseApiErrorSchema.extend({
  status: z.literal(500),
  code: z.literal('INTERNAL_SERVER_ERROR').optional(),
})

// Generic error response (fallback)
export const ApiErrorResponseSchema = BaseApiErrorSchema

// Success message response
export const ApiSuccessResponseSchema = z.object({
  message: z.string(),
  status: z.number().default(200),
  data: z.any().optional(),
})

// Union of all error types
export const ApiErrorUnionSchema = z.union([
  ApiValidationErrorSchema,
  ApiUnauthorizedErrorSchema,
  ApiForbiddenErrorSchema,
  ApiNotFoundErrorSchema,
  ApiRateLimitErrorSchema,
  ApiServerErrorSchema,
  ApiErrorResponseSchema,
])

// =====================================
// UNION TYPES
// =====================================

// Success or Error union
export const ApiResponseUnionSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.union([
    ApiSingleResponseSchema(dataSchema),
    ApiCollectionResponseSchema(dataSchema),
    ApiPaginatedResponseSchema(dataSchema),
    ApiValidationErrorSchema,
    ApiErrorResponseSchema,
    ApiSuccessResponseSchema,
  ])

// =====================================
// TYPE DEFINITIONS
// =====================================

export type ApiSingleResponse<T> = {
  data: T
}

export type ApiCollectionResponse<T> = {
  data: T[]
}

export type ApiPaginatedResponse<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
    from: number | null
    to: number | null
  }
}
export type ApiLookupResponse<T> = {
  list: T[]
  selected: T[]
}

export type BaseApiError = z.infer<typeof BaseApiErrorSchema>
export type ApiValidationError = z.infer<typeof ApiValidationErrorSchema>
export type ApiUnauthorizedError = z.infer<typeof ApiUnauthorizedErrorSchema>
export type ApiForbiddenError = z.infer<typeof ApiForbiddenErrorSchema>
export type ApiNotFoundError = z.infer<typeof ApiNotFoundErrorSchema>
export type ApiRateLimitError = z.infer<typeof ApiRateLimitErrorSchema>
export type ApiServerError = z.infer<typeof ApiServerErrorSchema>
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>
export type ApiSuccessResponse = z.infer<typeof ApiSuccessResponseSchema>

// =====================================
// UTILITY FUNCTIONS
// =====================================

// Extract data from single response
export const extractSingleData = <T>(response: ApiSingleResponse<T>): T => {
  return response.data
}

// Extract data from collection response
export const extractCollectionData = <T>(
  response: ApiCollectionResponse<T>
): T[] => {
  return response.data
}

// Extract data from paginated response
export const extractPaginatedData = <T>(
  response: ApiPaginatedResponse<T>
): T[] => {
  return response.data
}

// Check if response is any kind of error
export const isApiError = (response: unknown): response is BaseApiError => {
  if (typeof response !== 'object' || response === null) {
    return false
  }

  const obj = response as Record<string, unknown>
  return (
    typeof obj.message === 'string' &&
    typeof obj.status === 'number' &&
    !('data' in obj)
  )
}

// Specific error type checkers
export const isValidationError = (
  response: unknown
): response is ApiValidationError => {
  if (!isApiError(response)) return false
  return response.status === 422 && 'errors' in response
}

export const isUnauthorizedError = (
  response: unknown
): response is ApiUnauthorizedError => {
  if (!isApiError(response)) return false
  return response.status === 401
}

export const isForbiddenError = (
  response: unknown
): response is ApiForbiddenError => {
  if (!isApiError(response)) return false
  return response.status === 403
}

export const isNotFoundError = (
  response: unknown
): response is ApiNotFoundError => {
  if (!isApiError(response)) return false
  return response.status === 404
}

export const isRateLimitError = (
  response: unknown
): response is ApiRateLimitError => {
  if (!isApiError(response)) return false
  return response.status === 429
}

export const isServerError = (
  response: unknown
): response is ApiServerError => {
  if (!isApiError(response)) return false
  return response.status >= 500
}

// Check if response is paginated
export const isPaginatedResponse = <T>(
  response: unknown
): response is ApiPaginatedResponse<T> => {
  if (typeof response !== 'object' || response === null) {
    return false
  }

  const obj = response as Record<string, unknown>
  return (
    'data' in obj &&
    Array.isArray(obj.data) &&
    'meta' in obj &&
    typeof obj.meta === 'object' &&
    obj.meta !== null
  )
}

// Get user-friendly error message based on status
export const getErrorMessage = (error: BaseApiError): string => {
  const defaultMessages: Record<number, string> = {
    400: 'Loš zahtev. Proverite poslate podatke.',
    401: 'Niste autentifikovani. Molimo prijavite se.',
    403: 'Nemate dozvolu za ovu akciju.',
    404: 'Traženi resurs nije pronađen.',
    422: 'Podaci nisu validni. Proverite formu.',
    429: 'Previše zahteva. Pokušajte ponovo kasnije.',
    500: 'Greška na serveru. Pokušajte ponovo kasnije.',
    502: 'Server je nedostupan. Pokušajte ponovo.',
    503: 'Servis je trenutno nedostupan.',
  }

  return error.message || defaultMessages[error.status] || 'Neočekivana greška'
}

// =====================================
// VALIDATOR FUNCTIONS
// =====================================

// Validate single resource response
export const validateSingleResponse = <T>(
  schema: z.ZodType<T>,
  data: unknown
): T => {
  const responseSchema = ApiSingleResponseSchema(schema)
  const result = responseSchema.parse(data)
  return result.data as T
}

// Validate collection response
export const validateCollectionResponse = <T>(
  schema: z.ZodType<T>,
  data: unknown
): T[] => {
  const responseSchema = ApiCollectionResponseSchema(schema)
  const result = responseSchema.parse(data)
  return result.data
}

// Validate paginated response
export const validatePaginatedResponse = <T>(
  schema: z.ZodType<T>,
  data: unknown
): ApiPaginatedResponse<T> => {
  const responseSchema = ApiPaginatedResponseSchema(schema)
  return responseSchema.parse(data)
}

export const validateLookupResponse = <T>(
  schema: z.ZodType<T>,
  data: unknown
): ApiLookupResponse<T> => {
  const responseSchema = ApiLookupResponseSchema(schema)
  const parsed = responseSchema.parse(data)
  return {
    list: parsed.list,
    selected: parsed.selected,
  }
}

// Safe validation with error handling
export const safeValidateResponse = <T>(
  schema: z.ZodType<T>,
  data: unknown,
  type: 'single' | 'collection' | 'paginated' | 'lookup' = 'single'
) => {
  try {
    let responseSchema: z.ZodType<any>

    switch (type) {
      case 'single':
        responseSchema = ApiSingleResponseSchema(schema)
        break
      case 'collection':
        responseSchema = ApiCollectionResponseSchema(schema)
        break
      case 'paginated':
        responseSchema = ApiPaginatedResponseSchema(schema)
        break
      case 'lookup':
        responseSchema = ApiLookupResponseSchema(schema)
        break
    }

    const result = responseSchema.safeParse(data)

    if (result.success) {
      return {
        success: true,
        data: type === 'paginated' ? result.data : result.data.data,
        meta: type === 'paginated' ? result.data.meta : undefined,
        links: type === 'paginated' ? result.data.links : undefined,
        selected: type === 'lookup' ? result.data.selected : undefined,
        list: type === 'lookup' ? result.data.list : undefined,
      }
    }

    return {
      success: false,
      error: result.error.errors,
    }
  } catch (error) {
    return {
      success: false,
      error: [
        { message: 'Unexpected validation error', code: 'VALIDATION_FAILED' },
      ],
    }
  }
}

// =====================================
// TYPED RESPONSE CREATORS (za backend)
// =====================================

// Helper functions za kreiranje tipizovanih odgovora u API-ju
export const createSingleResponse = <T>(data: T): ApiSingleResponse<T> => ({
  data,
})

export const createCollectionResponse = <T>(
  data: T[]
): ApiCollectionResponse<T> => ({
  data,
})

export const createPaginatedResponse = <T>(
  data: T[],
  meta: ApiPaginatedResponse<T>['meta']
): ApiPaginatedResponse<T> => ({
  data,
  meta,
})

export const createErrorResponse = (
  message: string,
  status: number,
  code?: string,
  details?: any
): BaseApiError => ({
  message,
  status,
  code,
  details,
})

export const createValidationErrorResponse = (
  message: string,
  errors: Record<string, string[]>,
  status: 422 = 422
): ApiValidationError => ({
  message,
  status,
  errors,
})

export const createUnauthorizedErrorResponse = (
  message = 'Unauthorized',
  code: 'UNAUTHORIZED' = 'UNAUTHORIZED'
): ApiUnauthorizedError => ({
  message,
  status: 401,
  code,
})

export const createForbiddenErrorResponse = (
  message = 'Forbidden',
  code: 'FORBIDDEN' = 'FORBIDDEN'
): ApiForbiddenError => ({
  message,
  status: 403,
  code,
})

export const createNotFoundErrorResponse = (
  message = 'Not Found'
): ApiNotFoundError => ({
  message,
  status: 404,
  code: 'NOT_FOUND',
})

export const createRateLimitErrorResponse = (
  message = 'Rate limit exceeded',
  retryAfter?: number
): ApiRateLimitError => ({
  message,
  status: 429,
  code: 'RATE_LIMIT_EXCEEDED',
  retry_after: retryAfter,
})

export const createServerErrorResponse = (
  message = 'Internal Server Error',
  details?: any
): ApiServerError => ({
  message,
  status: 500,
  code: 'INTERNAL_SERVER_ERROR',
  details,
})
